/**
 * The Three.js side of the 3D viewer, kept out of React on purpose.
 *
 * React owns the UI state (which part is hovered, selected, how far the assembly is exploded); this
 * class owns the scene graph, the render loop and the GPU resources. The component talks to it
 * through plain setters and gets told about hover/selection through callbacks, so there is exactly
 * one place that knows about WebGL and exactly one place that knows about JSX.
 *
 * Two behaviours worth calling out:
 *
 *  * **Rendering is on demand.** A static assembly costs zero frames: a frame is only drawn when
 *    something actually changed (camera moved, selection changed, explosion animating).
 *  * **Materials are never mutated.** Highlighting swaps `mesh.material` between pre-built clones,
 *    so leaving a hover restores the exact previous appearance rather than trying to undo an edit.
 */

import {
  ACESFilmicToneMapping,
  AmbientLight,
  Box3,
  Color,
  DirectionalLight,
  Group,
  HemisphereLight,
  Material,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  PerspectiveCamera,
  Raycaster,
  Scene,
  Sphere,
  SRGBColorSpace,
  Texture,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three';
import {OrbitControls} from 'three/examples/jsm/controls/OrbitControls.js';
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader.js';

import {computeExplodeOffset, ExplodeLayout} from '@shared/lib/cad/explode';
import {resolvePartIdFromNode} from '@shared/lib/cad/node_resolution';
import {CadViewerPart, Vec3} from '@shared/models/cad_model';

import {
  toVec3,
  worldCenterOf,
  worldOffsetToLocal,
} from '@mavismart_frontend/lib/door_3d/explode_transform';

/** Capped so that a 3x DPR phone does not render 9x the pixels for no visible gain. */
const MAX_PIXEL_RATIO = 2;
/** Seconds the assembly takes to travel between two explosion ratios. */
const EXPLODE_ANIMATION_SECONDS = 0.55;
/** Camera framing margin: 1 = bounding sphere exactly fills the view. */
const FRAME_MARGIN = 1.25;

export interface ViewerTheme {
  /** Scene background. Should match the surrounding panel. */
  background: string;
  /** Selection colour. Always paired with an outline, never used on its own to convey selection. */
  selection: string;
  /** Hover colour. Deliberately much weaker than the selection colour. */
  hover: string;
}

export interface ViewerEngineCallbacks {
  onHoverChange: (partId: string | undefined) => void;
  onSelectChange: (partId: string | undefined) => void;
  onContextLost: () => void;
}

interface PartRuntime {
  id: string;
  nodeName: string;
  node: Object3D;
  /** Untouched local translation captured right after load: every offset starts from here. */
  originalPosition: Vector3;
  /** World-space centre of the part in the assembled state, used for the radial direction. */
  center: Vec3;
  selectable: boolean;
  fixed: boolean;
  explodeVector?: Vec3;
  explodeDistance: number;
  meshes: Mesh[];
  baseMaterial: Material;
  hoverMaterial: Material;
  selectedMaterial: Material;
}

/**
 * Subtle per-part hues used only when the model carries no colour of its own.
 *
 * The FreeCAD fallback reader cannot recover STEP colours, which leaves every part the same grey and
 * makes an exploded view of 19 parts unreadable. These are a *legibility aid*, not data: they carry
 * no business meaning and are disabled as soon as the GLB has real materials.
 */
const PART_TINTS = [
  0x9fb3c8, 0xc3b79a, 0x93b8a6, 0xc0a3b4, 0xa7a9c6, 0xc9b98f, 0x8fb6bd, 0xb4bc93, 0xb09cc2,
];

export interface LoadResult {
  /** Node names of the parts found in the GLB, in scene order. */
  nodeNames: string[];
  /** Whether the GLB carried more than one material, i.e. real colours. */
  hasOwnColors: boolean;
}

export class ViewerEngine {
  private readonly renderer: WebGLRenderer;
  private readonly scene: Scene;
  private readonly camera: PerspectiveCamera;
  private readonly controls: OrbitControls;
  private readonly raycaster = new Raycaster();
  private readonly pointer = new Vector2();
  private readonly resizeObserver: ResizeObserver;

  private modelRoot: Group | undefined;
  private parts: PartRuntime[] = [];
  private readonly partsById = new Map<string, PartRuntime>();
  private nodeNameToPartId: ReadonlyMap<string, string> = new Map();

  private layout: ExplodeLayout = {center: [0, 0, 0], diagonal: 1};
  private explodeRatio = 0;
  private explodeTarget = 0;
  private animating = false;

  private hoveredId: string | undefined;
  private selectedId: string | undefined;

  private renderRequested = false;
  private frameHandle: number | undefined;
  private lastFrameTime = 0;
  private disposed = false;
  private pointerInside = false;
  /** Guards against treating a camera drag as a click. */
  private pointerDownAt: {x: number; y: number} | undefined;

  public constructor(
    private readonly container: HTMLElement,
    private readonly theme: ViewerTheme,
    private readonly callbacks: ViewerEngineCallbacks
  ) {
    this.renderer = new WebGLRenderer({antialias: true, alpha: false, powerPreference: 'default'});
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO));
    this.renderer.outputColorSpace = SRGBColorSpace;
    this.renderer.toneMapping = ACESFilmicToneMapping;
    this.renderer.domElement.style.display = 'block';
    this.renderer.domElement.style.width = '100%';
    this.renderer.domElement.style.height = '100%';
    // Let the browser handle one-finger scrolling; OrbitControls takes the rest.
    this.renderer.domElement.style.touchAction = 'none';
    container.appendChild(this.renderer.domElement);

    this.scene = new Scene();
    this.scene.background = new Color(theme.background);

    this.camera = new PerspectiveCamera(45, 1, 0.01, 1000);
    // Position initiale arbitraire — frameAll() la remplace dès que le modèle est chargé.
    this.camera.position.set(0, 0, 5);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.12;
    this.controls.screenSpacePanning = true;
    this.controls.rotateSpeed = 1.5;
    this.controls.minPolarAngle = 0;
    this.controls.maxPolarAngle = Math.PI;
    this.controls.addEventListener('change', this.handleControlsChange);

    // Neutral, shadowless lighting. Kept deliberately restrained: enough contrast to read the shape
    // of a part, not so much that the assembly washes out to flat white.
    this.scene.add(new AmbientLight(0xffffff, 0.45));
    this.scene.add(new HemisphereLight(0xffffff, 0x7f8a99, 0.7));
    const key = new DirectionalLight(0xffffff, 1.15);
    key.position.set(4, 6, 5);
    this.scene.add(key);
    const fill = new DirectionalLight(0xffffff, 0.45);
    fill.position.set(-5, 2, -4);
    this.scene.add(fill);

    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(container);
    this.handleResize();

    const canvas = this.renderer.domElement;
    canvas.addEventListener('pointermove', this.handlePointerMove);
    canvas.addEventListener('pointerleave', this.handlePointerLeave);
    canvas.addEventListener('pointerdown', this.handlePointerDown);
    canvas.addEventListener('pointerup', this.handlePointerUp);
    canvas.addEventListener('webglcontextlost', this.handleContextLost);
  }

  //
  // LOADING
  //

  public async load(url: string, onProgress?: (ratio: number) => void): Promise<LoadResult> {
    const loader = new GLTFLoader();
    const gltf = await loader.loadAsync(url, event => {
      // `lengthComputable` is false when the server does not send Content-Length (gzipped
      // responses often do not), in which case there is simply no progress to report.
      if (event.lengthComputable && event.total > 0) {
        onProgress?.(event.loaded / event.total);
      }
    });
    if (this.disposed) {
      // The component unmounted while the model was in flight.
      this.disposeObject(gltf.scene);
      return {nodeNames: [], hasOwnColors: false};
    }

    const root = new Group();
    root.name = 'cad-model-root';
    // The STEP→GLB pipeline keeps the STEP convention (Z-up). Rotate +90° around X so that the
    // model's height aligns with Y (Three.js / glTF standard) and OrbitControls works naturally.
    root.rotation.x = Math.PI / 2;
    root.add(gltf.scene);
    this.scene.add(root);
    this.modelRoot = root;
    root.updateMatrixWorld(true);

    const nodeNames: string[] = [];
    const materials = new Set<Material>();
    gltf.scene.traverse(object => {
      if (isPartNode(object)) {
        nodeNames.push(object.name);
      }
      if (object instanceof Mesh) {
        for (const material of materialsOf(object)) {
          materials.add(material);
        }
      }
    });

    return {nodeNames, hasOwnColors: materials.size > 1};
  }

  /**
   * Binds the business parts to the loaded geometry.
   *
   * Called after `load`, once the page has resolved the manifest against the node names the GLB
   * actually contains. Parts the GLB does not have are ignored here: the mapping diagnostic in
   * `@shared/lib/cad/manifest_mapping` is what reports them.
   */
  public setParts(parts: readonly CadViewerPart[], opts: {applyTints: boolean}): void {
    const root = this.modelRoot;
    if (root === undefined) {
      return;
    }

    const byNodeName = new Map<string, Object3D>();
    root.traverse(object => {
      if (isPartNode(object) && !byNodeName.has(object.name)) {
        byNodeName.set(object.name, object);
      }
    });

    this.disposePartMaterials();
    this.parts = [];
    this.partsById.clear();

    const assemblyBox = new Box3();
    const scratchBox = new Box3();

    parts.forEach((part, index) => {
      const node = byNodeName.get(part.nodeName);
      if (node === undefined) {
        return;
      }
      const meshes: Mesh[] = [];
      node.traverse(child => {
        if (child instanceof Mesh) {
          meshes.push(child);
        }
      });
      if (meshes.length === 0) {
        return;
      }

      const original = meshes[0]?.material;
      const baseMaterial = cloneMaterial(original);
      if (opts.applyTints && baseMaterial instanceof MeshStandardMaterial) {
        baseMaterial.color = new Color(PART_TINTS[index % PART_TINTS.length] ?? 0xc0c0c0);
      }
      const hoverMaterial = cloneMaterial(baseMaterial);
      applyHighlight(hoverMaterial, this.theme.hover, 0.35);
      const selectedMaterial = cloneMaterial(baseMaterial);
      applyHighlight(selectedMaterial, this.theme.selection, 0.75);

      for (const mesh of meshes) {
        mesh.material = baseMaterial;
      }

      const runtime: PartRuntime = {
        id: part.id,
        nodeName: part.nodeName,
        node,
        originalPosition: node.position.clone(),
        center: toVec3(worldCenterOf(node, scratchBox)),
        selectable: part.selectable,
        fixed: part.fixed,
        explodeDistance: part.explodeDistance,
        meshes,
        baseMaterial,
        hoverMaterial,
        selectedMaterial,
      };
      if (part.explodeVector !== undefined) {
        runtime.explodeVector = part.explodeVector;
      }
      this.parts.push(runtime);
      this.partsById.set(part.id, runtime);
      assemblyBox.union(scratchBox);
    });

    this.nodeNameToPartId = new Map(this.parts.map(p => [p.nodeName, p.id]));

    if (!assemblyBox.isEmpty()) {
      const center = new Vector3();
      assemblyBox.getCenter(center);
      this.layout = {
        center: toVec3(center),
        diagonal: assemblyBox.min.distanceTo(assemblyBox.max),
      };
    }

    this.applyExplode(this.explodeRatio);
    this.requestRender();
  }

  //
  // CAMERA
  //

  private hasFramedOnce = false;

  /** Frames the whole assembly. Called after load and by the "recentre" button. */
  public frameAll(): void {
    const target = this.modelRoot;
    if (target === undefined) {
      return;
    }
    const box = new Box3().setFromObject(target, true);
    if (box.isEmpty()) {
      return;
    }
    const sphere = box.getBoundingSphere(new Sphere());
    const radius = Math.max(sphere.radius, 0.001);

    const fov = (this.camera.fov * Math.PI) / 180;
    const distance = (radius / Math.sin(fov / 2)) * FRAME_MARGIN;

    let direction: Vector3;
    if (!this.hasFramedOnce) {
      // First frame: 3/4 view from the left and slightly above to show the exploded assembly.
      direction = new Vector3(0.8, 0.3, 0.5).normalize();
      this.hasFramedOnce = true;
    } else {
      // "Recentre" button: keep the current viewing angle so the user's rotation is preserved.
      direction = new Vector3().subVectors(this.camera.position, this.controls.target).normalize();
      if (direction.lengthSq() < 1e-8) {
        direction = new Vector3(1, 0.5, 0.8).normalize();
      }
    }

    this.controls.target.copy(sphere.center);
    this.camera.position.copy(sphere.center).addScaledVector(direction, distance);
    this.camera.up.set(0, 1, 0);
    this.camera.near = Math.max(distance / 1000, 0.001);
    this.camera.far = distance * 10;
    this.camera.updateProjectionMatrix();
    this.controls.minDistance = radius * 0.2;
    this.controls.maxDistance = distance * 4;
    this.controls.update();
    this.requestRender();
  }

  /** Resets the orientation as well, used when the viewer is re-shown. */
  public resetView(): void {
    this.camera.up.set(0, 1, 0);
    this.camera.position.copy(this.controls.target).add(new Vector3(1, 0.05, 0));
    this.frameAll();
  }

  /** Frames a single part: centres on it and zooms to fit its bounding box, with animation. */
  public framePart(partId: string): void {
    const part = this.partsById.get(partId);
    if (part === undefined) {
      return;
    }
    const box = new Box3().setFromObject(part.node, true);
    if (box.isEmpty()) {
      return;
    }
    const sphere = box.getBoundingSphere(new Sphere());
    const radius = Math.max(sphere.radius, 0.001);

    const fov = (this.camera.fov * Math.PI) / 180;
    const distance = (radius / Math.sin(fov / 2)) * FRAME_MARGIN * 1.8;

    // Keep the current viewing angle, just move closer to the part.
    const direction = new Vector3()
      .subVectors(this.camera.position, this.controls.target)
      .normalize();
    if (direction.lengthSq() < 1e-8) {
      direction.set(1, 0.3, 0.5).normalize();
    }

    const targetPosition = sphere.center.clone();
    const cameraPosition = sphere.center.clone().addScaledVector(direction, distance);

    this.animateCamera(cameraPosition, targetPosition, distance);
  }

  /** Smoothly animates the camera to a new position and target. */
  private cameraAnimation:
    | {
        start: number;
        duration: number;
        from: {pos: Vector3; target: Vector3};
        to: {pos: Vector3; target: Vector3};
        toDistance: number;
      }
    | undefined;

  private animateCamera(toPosition: Vector3, toTarget: Vector3, toDistance: number): void {
    this.cameraAnimation = {
      start: performance.now(),
      duration: 400,
      from: {
        pos: this.camera.position.clone(),
        target: this.controls.target.clone(),
      },
      to: {pos: toPosition, target: toTarget},
      toDistance,
    };
    this.requestRender();
  }

  //
  // NAVIGATION MODE
  //

  /**
   * Changes the primary mouse-button action.
   * - 'orbit': left-drag rotates
   * - 'pan': left-drag translates the camera in the screen plane
   * - 'zoom': left-drag dollies in/out
   */
  public setNavMode(mode: 'orbit' | 'pan' | 'zoom'): void {
    // Ensure the up vector is canonical so OrbitControls behaves predictably.
    this.camera.up.set(0, 1, 0);

    // THREE.MOUSE enum values: ROTATE=0, DOLLY=1, PAN=2
    const ROTATE = 0;
    const DOLLY = 1;
    const PAN = 2;
    switch (mode) {
      case 'orbit':
        this.controls.mouseButtons = {LEFT: ROTATE, MIDDLE: DOLLY, RIGHT: PAN};
        break;
      case 'pan':
        this.controls.mouseButtons = {LEFT: PAN, MIDDLE: DOLLY, RIGHT: ROTATE};
        break;
      case 'zoom':
        this.controls.mouseButtons = {LEFT: DOLLY, MIDDLE: DOLLY, RIGHT: PAN};
        break;
    }
  }

  /** Programmatic zoom: positive = closer, negative = further. */
  public zoom(factor: number): void {
    const direction = new Vector3()
      .subVectors(this.controls.target, this.camera.position)
      .normalize();
    const distance = this.camera.position.distanceTo(this.controls.target);
    const step = distance * factor * 0.25;
    this.camera.position.addScaledVector(direction, step);
    this.controls.update();
    this.requestRender();
  }

  /**
   * Rotates the camera horizontally around the target by `angleDeg` degrees.
   * Positive = counter-clockwise (to the left when viewed from above).
   * This is rotation around the Y axis (green).
   */
  public rotateAzimuth(angleDeg: number): void {
    const angleRad = (angleDeg * Math.PI) / 180;
    const offset = new Vector3().subVectors(this.camera.position, this.controls.target);
    // Rotate around Y axis (vertical)
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);
    const x = offset.x * cos + offset.z * sin;
    const z = -offset.x * sin + offset.z * cos;
    offset.x = x;
    offset.z = z;
    this.camera.position.copy(this.controls.target).add(offset);
    this.camera.lookAt(this.controls.target);
    this.controls.update();
    this.requestRender();
  }

  /**
   * Rotates the camera vertically around the target by `angleDeg` degrees.
   * Positive = tilt up (camera goes higher). This is rotation around the X axis (red) from
   * the camera's perspective — technically around the camera's "right" vector.
   */
  public rotatePolar(angleDeg: number): void {
    const angleRad = (angleDeg * Math.PI) / 180;
    const offset = new Vector3().subVectors(this.camera.position, this.controls.target);
    // The "right" axis of the camera is its local X, which is cross(up, viewDirection).
    const viewDir = new Vector3()
      .subVectors(this.controls.target, this.camera.position)
      .normalize();
    const right = new Vector3().crossVectors(this.camera.up, viewDir).normalize();
    // Rotate offset around this right axis
    offset.applyAxisAngle(right, angleRad);
    this.camera.position.copy(this.controls.target).add(offset);
    this.camera.lookAt(this.controls.target);
    this.controls.update();
    this.requestRender();
  }

  /** Snaps the camera to a preset orientation, keeping the current target (assembly centre). */
  public setPresetView(preset: 'front' | 'back' | 'top' | 'bottom' | 'right' | 'left'): void {
    const target = this.controls.target.clone();
    const distance = this.camera.position.distanceTo(target);
    const safeDistance = Math.max(distance, 0.5);

    let direction: Vector3;
    switch (preset) {
      case 'front':
        direction = new Vector3(1, 0, 0);
        break;
      case 'back':
        direction = new Vector3(-1, 0, 0);
        break;
      case 'top':
        direction = new Vector3(0.001, 1, 0); // tiny offset avoids gimbal lock
        break;
      case 'bottom':
        direction = new Vector3(0.001, -1, 0);
        break;
      case 'right':
        direction = new Vector3(0, 0, -1);
        break;
      case 'left':
        direction = new Vector3(0, 0, 1);
        break;
    }

    this.camera.position.copy(target).addScaledVector(direction, safeDistance);
    this.camera.up.set(0, 1, 0);
    this.camera.lookAt(target);
    this.controls.update();
    this.requestRender();
  }

  //
  // EXPLODE
  //

  public setExplodeRatio(ratio: number, opts?: {animate?: boolean}): void {
    this.explodeTarget = ratio;
    if (opts?.animate === false) {
      this.explodeRatio = ratio;
      this.applyExplode(ratio);
      this.requestRender();
      return;
    }
    if (!this.animating) {
      this.animating = true;
      this.lastFrameTime = performance.now();
      this.requestRender();
    }
  }

  public getExplodeRatio(): number {
    return this.explodeRatio;
  }

  public getSelectedPartId(): string | undefined {
    return this.selectedId;
  }

  /**
   * The scene node backing a business part.
   *
   * Exposed so that later features can act on a part's geometry — framing the camera on the part an
   * alert points at, for instance — without this class having to know about them.
   */
  public getPartNode(partId: string): Object3D | undefined {
    return this.partsById.get(partId)?.node;
  }

  /**
   * Positions every part for a given ratio.
   *
   * Always recomputed from `originalPosition`, never from the current position, so moving the slider
   * around cannot accumulate drift.
   */
  private applyExplode(ratio: number): void {
    for (const part of this.parts) {
      const worldOffset = computeExplodeOffset(
        {
          center: part.center,
          fixed: part.fixed,
          explodeDistance: part.explodeDistance,
          ...(part.explodeVector === undefined ? {} : {explodeVector: part.explodeVector}),
        },
        this.layout,
        ratio
      );
      const localOffset = worldOffsetToLocal(part.node, worldOffset);
      // Only the translation is touched: rotation and scale are left exactly as authored.
      part.node.position.copy(part.originalPosition).add(localOffset);
    }
  }

  //
  // HOVER / SELECTION
  //

  public setHovered(partId: string | undefined): void {
    if (this.hoveredId === partId) {
      return;
    }
    this.hoveredId = partId;
    this.refreshMaterials();
    this.requestRender();
  }

  public setSelected(partId: string | undefined): void {
    if (this.selectedId === partId) {
      return;
    }
    this.selectedId = partId;
    this.refreshMaterials();
    this.requestRender();
  }

  /** Recomputes which of the three cached materials each part should be showing. */
  private refreshMaterials(): void {
    for (const part of this.parts) {
      const material =
        part.id === this.selectedId
          ? part.selectedMaterial
          : part.id === this.hoveredId
            ? part.hoverMaterial
            : part.baseMaterial;
      for (const mesh of part.meshes) {
        mesh.material = material;
      }
    }
  }

  private pickPartAt(clientX: number, clientY: number): string | undefined {
    const root = this.modelRoot;
    if (root === undefined) {
      return undefined;
    }
    const rect = this.renderer.domElement.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      return undefined;
    }
    this.pointer.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1
    );
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hits = this.raycaster.intersectObject(root, true);
    for (const hit of hits) {
      // A hit lands on a mesh, which is a technical child of the node carrying the business id.
      const partId = resolvePartIdFromNode(hit.object, this.nodeNameToPartId);
      if (partId === undefined) {
        continue;
      }
      if (this.partsById.get(partId)?.selectable === true) {
        return partId;
      }
      // A non-selectable part must not block what is behind it either.
    }
    return undefined;
  }

  //
  // EVENTS
  //

  private readonly handleControlsChange = (): void => {
    this.requestRender();
  };

  private readonly handleResize = (): void => {
    const {clientWidth, clientHeight} = this.container;
    if (clientWidth === 0 || clientHeight === 0) {
      return;
    }
    this.renderer.setSize(clientWidth, clientHeight, false);
    this.camera.aspect = clientWidth / clientHeight;
    this.camera.updateProjectionMatrix();
    this.requestRender();
  };

  private readonly handlePointerMove = (event: PointerEvent): void => {
    this.pointerInside = true;
    // Hover feedback on a touch drag would fight with the camera gesture.
    if (event.pointerType === 'touch') {
      return;
    }
    const partId = this.pickPartAt(event.clientX, event.clientY);
    this.renderer.domElement.style.cursor = partId === undefined ? 'grab' : 'pointer';
    if (partId !== this.hoveredId) {
      this.setHovered(partId);
      this.callbacks.onHoverChange(partId);
    }
  };

  private readonly handlePointerLeave = (): void => {
    this.pointerInside = false;
    if (this.hoveredId !== undefined) {
      this.setHovered(undefined);
      this.callbacks.onHoverChange(undefined);
    }
  };

  private readonly handlePointerDown = (event: PointerEvent): void => {
    this.pointerDownAt = {x: event.clientX, y: event.clientY};
  };

  private readonly handlePointerUp = (event: PointerEvent): void => {
    const down = this.pointerDownAt;
    this.pointerDownAt = undefined;
    if (down === undefined) {
      return;
    }
    // A drag orbits the camera; only a near-stationary press counts as a selection.
    const moved = Math.hypot(event.clientX - down.x, event.clientY - down.y);
    if (moved > 6) {
      return;
    }
    const partId = this.pickPartAt(event.clientX, event.clientY);
    this.setSelected(partId);
    this.callbacks.onSelectChange(partId);
  };

  private readonly handleContextLost = (event: Event): void => {
    // Preventing the default lets the browser attempt a restore, but the viewer still needs to tell
    // the user why the picture froze.
    event.preventDefault();
    this.callbacks.onContextLost();
  };

  //
  // RENDER LOOP
  //

  /** Schedules exactly one frame. A still scene therefore costs nothing. */
  public requestRender(): void {
    if (this.disposed || this.renderRequested) {
      return;
    }
    this.renderRequested = true;
    this.frameHandle = requestAnimationFrame(this.renderFrame);
  }

  private readonly renderFrame = (now: number): void => {
    this.renderRequested = false;
    if (this.disposed) {
      return;
    }

    let needsAnotherFrame = false;

    if (this.animating) {
      const elapsed = Math.max(0, now - this.lastFrameTime) / 1000;
      this.lastFrameTime = now;
      const step = elapsed / EXPLODE_ANIMATION_SECONDS;
      const delta = this.explodeTarget - this.explodeRatio;
      if (Math.abs(delta) <= step || step >= 1) {
        this.explodeRatio = this.explodeTarget;
        this.animating = false;
      } else {
        this.explodeRatio += Math.sign(delta) * step;
        needsAnotherFrame = true;
      }
      this.applyExplode(this.explodeRatio);
    }

    // Camera fly-to animation (triggered by framePart).
    if (this.cameraAnimation !== undefined) {
      const anim = this.cameraAnimation;
      const t = Math.min((now - anim.start) / anim.duration, 1);
      // Ease-out cubic for a smooth deceleration.
      const ease = 1 - (1 - t) ** 3;

      this.camera.position.lerpVectors(anim.from.pos, anim.to.pos, ease);
      this.controls.target.lerpVectors(anim.from.target, anim.to.target, ease);
      this.controls.update();

      if (t >= 1) {
        // Finalise: update near/far and distance limits for the new framing.
        const distance = anim.toDistance;
        this.camera.near = Math.max(distance / 1000, 0.001);
        this.camera.far = distance * 10;
        this.camera.updateProjectionMatrix();
        this.controls.minDistance = distance * 0.05;
        this.controls.maxDistance = distance * 8;
        this.cameraAnimation = undefined;
      } else {
        needsAnotherFrame = true;
      }
    }

    // Damping keeps easing the camera for a few frames after the pointer is released.
    if (this.controls.update()) {
      needsAnotherFrame = true;
    }

    this.renderer.render(this.scene, this.camera);

    if (needsAnotherFrame) {
      this.requestRender();
    }
  };

  //
  // TEARDOWN
  //

  public dispose(): void {
    this.disposed = true;
    if (this.frameHandle !== undefined) {
      cancelAnimationFrame(this.frameHandle);
    }
    this.resizeObserver.disconnect();

    const canvas = this.renderer.domElement;
    canvas.removeEventListener('pointermove', this.handlePointerMove);
    canvas.removeEventListener('pointerleave', this.handlePointerLeave);
    canvas.removeEventListener('pointerdown', this.handlePointerDown);
    canvas.removeEventListener('pointerup', this.handlePointerUp);
    canvas.removeEventListener('webglcontextlost', this.handleContextLost);

    this.controls.removeEventListener('change', this.handleControlsChange);
    this.controls.dispose();

    this.disposePartMaterials();
    if (this.modelRoot !== undefined) {
      this.disposeObject(this.modelRoot);
      this.scene.remove(this.modelRoot);
      this.modelRoot = undefined;
    }
    this.disposeObject(this.scene);
    this.parts = [];
    this.partsById.clear();

    this.renderer.dispose();
    // Frees the GPU context immediately rather than waiting for the GC.
    this.renderer.forceContextLoss();
    if (canvas.parentNode !== null) {
      canvas.parentNode.removeChild(canvas);
    }
  }

  /** Disposes the clones this class created. Materials owned by the GLTF are freed separately. */
  private disposePartMaterials(): void {
    for (const part of this.parts) {
      part.baseMaterial.dispose();
      part.hoverMaterial.dispose();
      part.selectedMaterial.dispose();
    }
  }

  private disposeObject(root: Object3D): void {
    root.traverse(object => {
      if (object instanceof Mesh) {
        object.geometry.dispose();
        for (const material of materialsOf(object)) {
          disposeMaterial(material);
        }
      }
    });
  }

  public isPointerInside(): boolean {
    return this.pointerInside;
  }
}

//
// HELPERS
//

/** A part node is one the converter marked as selectable in the glTF `extras`. */
function isPartNode(object: Object3D): boolean {
  const extras = object.userData as {selectablePart?: unknown} | undefined;
  return extras?.selectablePart === true && object.name !== '';
}

function materialsOf(mesh: Mesh): Material[] {
  return Array.isArray(mesh.material) ? mesh.material : [mesh.material];
}

function cloneMaterial(material: Material | Material[] | undefined): Material {
  const source = Array.isArray(material) ? material[0] : material;
  if (source === undefined) {
    return new MeshStandardMaterial({color: 0xb9bcc0, metalness: 0.15, roughness: 0.65});
  }
  return source.clone();
}

/**
 * Builds a highlight variant of a material.
 *
 * Emissive is used rather than a base-colour change so that the part's own shading, and any real
 * STEP colour, still shows through: the highlight reads as "lit up", not "repainted".
 */
function applyHighlight(material: Material, color: string, strength: number): void {
  if (material instanceof MeshStandardMaterial) {
    material.emissive = new Color(color);
    material.emissiveIntensity = strength;
  }
}

/**
 * Disposes a material and every texture it references.
 *
 * `material.dispose()` alone leaks texture memory, and texture slots differ per material type, so
 * the material's own properties are what gets scanned.
 */
function disposeMaterial(material: Material): void {
  for (const value of Object.values(material as unknown as Record<string, unknown>)) {
    if (value instanceof Texture) {
      value.dispose();
    }
  }
  material.dispose();
}
